import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query';
import {
  acceptTermsOfService,
  authorizeGustoForAccount,
  calculatePayroll,
  checkPayrollReady,
  completeOnboarding,
  connectBank,
  createGustoCompany,
  createLinkTokenForGusto,
  createPayrollFlow,
  createPayrollRequest,
  createWebhook,
  deleteGustoAuthorization,
  downloadCompanyFormPdf,
  downloadPayStub,
  getAcceptTermsOfServiceStatus,
  getAuthorizationStatusForAccount,
  getAuthorizeURLForBusiness,
  getAvailableW2Documents,
  getBankAccounts,
  getCompanyForms,
  getCurrentGustoAccount,
  getEmployeeDocument,
  getEmployees,
  getGustoCompanies,
  GetGustoCompaniesParams,
  getGustoTokenHealth,
  getOfficers,
  getOnboardingFlow,
  getPayrollOnboardingStatus,
  getPayrolls,
  GetPayrollsParams,
  getUpcomingPayrolls,
  getWebhook,
  GustoAccountResp,
  GustoCompanyResp,
  GustoTokenHealthResp,
  migrateEmbeddedPayroll,
  PayrollRequest,
  saveOfficers,
  setDisabledPayrollFeature,
  signCompanyForm,
  startCustomerOnboarding,
  updatePayrollOnboardingStatus,
} from 'services/payroll';
import { queryClient } from 'states/reactQueryClient';
import {
  Employee,
  Payroll,
  PayrollFlowType,
  PayrollOnboardingStatus,
  SignCompanyFormBody,
} from 'models/payroll';
import { ApiListResp, getDefaultApiListResp } from 'models/api';
import { isUndefined, omitBy } from 'lodash';
import { defaultTablePageSize } from 'constants/common';

type GustoAccount = GustoAccountResp;
export const useGustoCurrentUsers = (
  queryProps?: UseQueryOptions<GustoAccount[]>,
) => {
  const { data, ...rest } = useQuery<GustoAccount[]>(
    ['gustoCurrentAccount'],
    () => getCurrentGustoAccount(),
    queryProps,
  );
  return {
    currentGustoUsers: data ?? [],
    ...rest,
  };
};

export const useGustoTokenHealth = (
  businessID: string,
  queryProps?: UseQueryOptions<GustoTokenHealthResp>,
) => {
  const { data, ...rest } = useQuery<GustoTokenHealthResp>(
    ['gustoTokenHealth', businessID],
    () => getGustoTokenHealth(businessID),
    {
      ...queryProps,
      enabled: !!businessID,
    },
  );
  return {
    tokenStatus: data,
    ...rest,
  };
};

export type GustoCompany = GustoCompanyResp;
export const useGustoCompanies = (
  params?: GetGustoCompaniesParams,
  queryProps?: UseQueryOptions<GustoCompany[]>,
) => {
  const { data, ...rest } = useQuery<GustoCompany[]>(
    ['gustoCompanies'],
    () => getGustoCompanies(params),
    queryProps,
  );
  return {
    gustoCompanies: data,
    ...rest,
  };
};

export const useCreateGustoCompany = (
  businessId: string,
  createProps: UseMutationOptions = {},
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    () => createGustoCompany(businessId),
    {
      onSuccess: () => queryClient.invalidateQueries(['business', businessId]),
      ...createProps,
    },
  );
  return {
    createGustoCompany: mutate,
    createGustoCompanyAsync: mutateAsync,
    ...rest,
  };
};

const defaultPayrollsResult = getDefaultApiListResp<Payroll[]>();
export const usePayrolls = (
  businessID?: string,
  params?: GetPayrollsParams,
  options?: UseQueryOptions<ApiListResp<Payroll[]>>,
) => {
  const { page = 1, size = defaultTablePageSize, from, to } = params ?? {};
  const { data = defaultPayrollsResult, ...rest } = useQuery<
    ApiListResp<Payroll[]>
  >(
    ['payrolls', businessID, page, size, from, to],
    () => getPayrolls(businessID!, omitBy(params, isUndefined)),
    {
      enabled: businessID != null,
      keepPreviousData: true,
      ...options,
    },
  );

  return {
    payrolls: data,
    ...rest,
  };
};

export const usePayStubDownload = (
  businessID: string | undefined,
  payrollUUID: string | undefined,
  employeeUUID: string | undefined,
  options?: UseQueryOptions<Blob>,
) =>
  useQuery<Blob>(
    ['downloadPayroll', businessID, payrollUUID, employeeUUID],
    () => downloadPayStub(businessID!, payrollUUID!, employeeUUID!),
    {
      enabled:
        businessID != null &&
        payrollUUID != null &&
        employeeUUID != null &&
        employeeUUID !== '',
      keepPreviousData: true,
      ...options,
    },
  );

export const useUpcomingPayrolls = (
  businessID: string | undefined,
  options?: UseQueryOptions<Payroll[]>,
) => {
  const { data = [], ...rest } = useQuery<Payroll[]>(
    ['upcomingPayrolls', businessID],
    () => getUpcomingPayrolls(businessID!),
    {
      enabled: businessID != null,
      keepPreviousData: true,
      ...options,
    },
  );

  return {
    payrolls: data,
    ...rest,
  };
};

export const useCalculatePayroll = (
  businessID: string,
  payrollUUID: string,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    () => calculatePayroll(businessID, payrollUUID),
    {},
  );
  return {
    calculatePayroll: mutate,
    calculatePayrollAsync: mutateAsync,
    ...rest,
  };
};

export const usePlaidLinkTokenForGusto = (businessID: string) =>
  useQuery<{ linkToken: string }>(['linkToken', businessID], () =>
    createLinkTokenForGusto(businessID).then((resp) => ({
      linkToken: resp.linkToken,
    })),
  );

interface ConnectBankParams {
  publicToken: string;
  bankName: string;
  bankId: string;
}
export const useConnectBank = (businessID: string | undefined) => {
  const { mutate, mutateAsync, ...rest } = useMutation<
    unknown,
    unknown,
    ConnectBankParams
  >(
    ['connect-bank', businessID],
    ({ publicToken, bankName, bankId }) => {
      if (!businessID) throw Error('Company UUID not found');
      return connectBank(businessID, publicToken, bankName, bankId);
    },
    {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries([
            'payroll-onboarding-status',
            businessID!,
          ]),
          queryClient.invalidateQueries(['company-bank-account', businessID!]),
        ]),
    },
  );

  return { connectBank: mutate, connectBankAsync: mutateAsync, ...rest };
};

export const usePayrollOnboardingStatus = (
  businessId?: string,
  options: UseQueryOptions<PayrollOnboardingStatus> = {},
) => {
  const { data, ...rest } = useQuery<PayrollOnboardingStatus>(
    ['payroll-onboarding-status', businessId],
    () => getPayrollOnboardingStatus(businessId!),
    {
      enabled: !!businessId,
      ...options,
    },
  );

  return {
    onboardingStatus: data ?? {
      onboardingCompleted: false,
      companyApproved: false,
      onboardingSteps: [],
      isStepCompleted: () => false,
    },
    ...rest,
  };
};

export const useCompanyBankAccount = (businessID: string) => {
  const { data, ...rest } = useQuery(
    ['company-bank-account', businessID],
    () => getBankAccounts(businessID),
    {
      enabled: businessID != null,
    },
  );
  return {
    bankAccounts: data,
    ...rest,
  };
};

export const useAcceptTermsOfServiceStatus = (
  businessId: string | undefined,
) => {
  const { data, ...rest } = useQuery(
    ['terms-of-service-status', businessId],
    () => getAcceptTermsOfServiceStatus(businessId!),
    {
      enabled: !!businessId,
    },
  );
  return {
    accepted: data,
    ...rest,
  };
};

export const useAcceptTermsOfService = (businessId?: string) => {
  const { mutateAsync, mutate, ...rest } = useMutation(
    ['accept-terms', businessId],
    () => {
      if (businessId == null) {
        throw new Error('cannot accept terms of service: company not found');
      }
      return acceptTermsOfService(businessId);
    },
    {
      onSuccess: () =>
        queryClient.invalidateQueries(['terms-of-service-status', businessId]),
    },
  );
  return {
    acceptTermsOfService: mutate,
    acceptTermsOfServiceAsync: mutateAsync,
    ...rest,
  };
};

export const useMigrateToEmbeddedPayroll = (
  businessId: string,
  options?: UseMutationOptions<unknown, unknown, unknown>,
) => {
  const { mutateAsync, mutate, ...rest } = useMutation(
    ['migrate-ep', businessId],
    () => {
      if (!businessId) {
        throw new Error('could not find company');
      }
      return migrateEmbeddedPayroll(businessId);
    },
    options,
  );
  return {
    migrateToEmbeddedPayroll: mutate,
    migrateToEmbeddedPayrollAsync: mutateAsync,
    ...rest,
  };
};

export const usePayrollOnboardingFlow = (businessId: string) => {
  const { data, ...rest } = useQuery(
    ['payroll-onboarding', businessId],
    () => getOnboardingFlow(businessId),
    { enabled: !!businessId },
  );
  return {
    onboardingUrl: data,
    ...rest,
  };
};

export const usePayrollReady = (businessId: string) =>
  useQuery(['payroll-ready', businessId], () => checkPayrollReady(businessId));

export const useStartCustomerOnboarding = (businessId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ['start-customer-onboarding', businessId],
    () => startCustomerOnboarding(businessId),
  );

  return {
    startCustomerOnboarding: mutate,
    startCustomerOnboardingAsync: mutateAsync,
    ...rest,
  };
};

export const useReviewAndConfirm = (
  mutationProps?: UseMutationOptions<unknown, unknown, unknown>,
) =>
  useMutation(
    (businessId: string) =>
      updatePayrollOnboardingStatus(businessId, 'CustomerDataConfirmed'),
    mutationProps,
  );

export const useCompanyForms = (businessId: string) => {
  const { data, ...rest } = useQuery(['company-forms', businessId], () =>
    getCompanyForms(businessId),
  );
  return {
    forms: data,
    ...rest,
  };
};

export const useDownloadCompanyForm = (businessId: string, formId: string) =>
  useQuery(['download-company-form', businessId, formId], () =>
    downloadCompanyFormPdf(businessId, formId),
  );

export const useSignCompanyForm = (businessId: string, formId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ['sign-company-form', businessId, formId],
    (data: SignCompanyFormBody) => signCompanyForm(businessId, formId, data),
  );
  return {
    signCompanyForm: mutate,
    signCompanyFormAsync: mutateAsync,
    ...rest,
  };
};

export const useCompleteOnboarding = (
  businessId: string,
  options: UseMutationOptions = {},
) => {
  const { mutateAsync, mutate, ...rest } = useMutation(
    ['complete-onboarding', businessId],
    () => completeOnboarding(businessId),
    {
      onSuccess: () =>
        queryClient.invalidateQueries([
          'payroll-onboarding-status',
          businessId,
        ]),
      ...options,
    },
  );
  return {
    completeOnboarding: mutate,
    completeOnboardingAsync: mutateAsync,
    ...rest,
  };
};

export const useGustoWebhooks = () => {
  const { data, ...rest } = useQuery(['gusto-webhook'], () => getWebhook());
  return {
    webhooks: data,
    ...rest,
  };
};

export const useCreateGustoWebhooks = () => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ['create-gusto-webhook'],
    createWebhook,
  );
  return {
    createWebhook: mutate,
    createWebhookAsync: mutateAsync,
    ...rest,
  };
};

export const useCreatePayrollFlow = (
  businessId: string,
  flowType: PayrollFlowType,
) => {
  const { data, ...rest } = useQuery(['create-payroll-flow', businessId], () =>
    createPayrollFlow(businessId, flowType),
  );
  return {
    url: data,
    ...rest,
  };
};

export const useAuthorizationStatusForAccount = (accountId: string) => {
  const { data, ...rest } = useQuery(
    ['authorization-gusto-status', accountId],
    () => getAuthorizationStatusForAccount(accountId),
    {
      enabled: accountId !== '',
    },
  );

  return {
    authorizationStatus: data,
    ...rest,
  };
};

export const useGetAuthorizeURLForBusiness = (businessId: string) => {
  const { data, ...rest } = useMutation(['get-authorize-url', businessId], () =>
    getAuthorizeURLForBusiness(businessId),
  );
  return {
    authorizeUrl: data,
    ...rest,
  };
};

export const useAuthorizeGustoForAccount = (businessId: string) => {
  const { mutateAsync, mutate, ...rest } = useMutation(
    ['authorize-gusto', businessId],
    ({ code, state }: { code: string; state: string }) =>
      authorizeGustoForAccount(businessId, code, state),
  );
  return {
    authorizeGustoForAccount: mutate,
    authorizeGustoForAccountAsync: mutateAsync,
    ...rest,
  };
};

export const useDisconnectGustoCompany = (businessId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ['disconnect-gusto-company', businessId],
    () => deleteGustoAuthorization(businessId),
    {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries([
            'authorization-gusto-status',
            businessId,
          ]),
          queryClient.invalidateQueries(['gustoTokenHealth', businessId]),
          queryClient.invalidateQueries(['company', businessId]),
        ]),
    },
  );
  return {
    disconnectGustoCompany: mutate,
    disconnectGustoCompanyAsync: mutateAsync,
    ...rest,
  };
};

export const useCreatePayrollRequest = (businessId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ['create-payroll-request', businessId],
    (request: PayrollRequest) => createPayrollRequest(businessId, request),
  );

  return {
    createPayrollRequest: mutate,
    createPayrollRequestAsync: mutateAsync,
    ...rest,
  };
};

export const useEmployees = (
  businessId: string,
  options: UseQueryOptions<unknown, unknown, Employee[]> = {},
) => {
  const { data, ...rest } = useQuery<unknown, unknown, Employee[]>(
    ['employees', businessId],
    () => getEmployees(businessId),
    options,
  );
  return {
    employees: data ?? [],
    ...rest,
  };
};

export const useW2AvailableDocuments = (
  businessId: string,
  employeeId: string,
) => {
  const { data, ...rest } = useQuery(
    ['w2-documents', businessId, employeeId],
    () => getAvailableW2Documents(businessId, employeeId),
    {
      enabled: !!businessId && !!employeeId,
    },
  );

  return {
    w2Documents: data,
    ...rest,
  };
};

export const useEmployeeDocument = (
  businessId: string,
  employeeId: string,
  documentUUID: string,
) => {
  const { data, ...rest } = useQuery(
    ['employee-document', businessId, employeeId, documentUUID],
    () => getEmployeeDocument(businessId, employeeId, documentUUID),
    {
      enabled: !!businessId && !!employeeId && !!documentUUID,
    },
  );
  return {
    document: data,
    ...rest,
  };
};

export const useOfficers = (businessID: string) => {
  const { data, ...rest } = useQuery(['officers', businessID], () =>
    getOfficers(businessID),
  );
  return {
    officers: data ?? [],
    ...rest,
  };
};

export const useSaveOfficers = (businessID: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    ['save-officers', businessID],
    (officers: string[]) => saveOfficers(businessID, officers),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['officers', businessID]);
      },
    },
  );

  return {
    saveOfficers: mutate,
    saveOfficersAsync: mutateAsync,
    ...rest,
  };
};

export const useSetDisabledPayrollFeature = (businessID: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation(
    (disabledPayroll: boolean) =>
      setDisabledPayrollFeature(businessID, disabledPayroll),
  );
  return {
    setDisabledPayrollFeature: mutate,
    setDisabledPayrollFeatureAsync: mutateAsync,
    ...rest,
  };
};
